import React from 'react';
import { useState } from 'react';
import { X, Send, Shield, Mail, MapPin, Phone, AlertTriangle, CheckCircle2 } from 'lucide-react';

const ContactUs = ({ contactModalOpen, setContactModalOpen }) => { 
    const serviceTypes = [
        'Technical Support',             
        'Account Issues',               
        'Billing Inquiries',          
        'Service Feedback',           
        'Feature Requests',            
        'Reporting Abuse',               
        'Security Concerns',           
        'Compliance Inquiries',       
        'General Questions',          
        'Business Partnerships',      
        'Consultation Services',        
        'Logout Issues',                
        'Content Inquiries',             
        'Feedback on User Experience',
        'Threat Intelligence',
        'Incident Response',
        'Penetration Testing',
        'Security Consulting',
        'Managed Security Services',
        'Compliance',
        'Vulnerability Assessment',
        'Security Awareness Training',
        'Risk Assessment',
        'Malware Analysis',
        'Forensics',
        'Data Protection',
        'Audit Services',
        'Network Security',
        'Cloud Security',
        'Disaster Recovery Planning',    
    ];
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    companyName: '', 
    serviceType: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);


  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full Name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
      isValid = false;
    }

    if (!formData.serviceType) {
      newErrors.serviceType = 'Service Type is required';
      isValid = false;
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
      isValid = false;
    } else if (formData.message.trim().length < 15) { 
      newErrors.message = 'Message must be at least 15 characters';
      isValid = false;
    } else if (formData.message.trim().length > 10000) { 
      newErrors.message = 'Message cannot exceed 10,000 characters';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target; // Use 'name' instead of 'id'
    setFormData(prev => ({
        ...prev,
        [name]: value // Now this will correctly match the input element's name
    }));

    // Clear error when user starts typing
    if (errors[name]) { // Also change 'id' to 'name' here
        setErrors(prev => ({
            ...prev,
            [name]: null
        }));
    }
};

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    if (validateForm()) {
      try {
        const response = await fetch('/uss/contactUs', { 
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          setFormData({
            fullName: '',
            email: '',
            companyName: '',
            serviceType: '',
            message: '',
          });
          setFormSubmitted(true);
          setSubmissionSuccess(true);
          setErrors({});
        } else {
          const data = await response.json();
          setErrors({ 
            submit: data.error || 'Failed to submit form. Please try again.' 
          });
        }
      } catch (err) {
        setErrors({ 
          submit: 'Network error. Please check your connection and try again.' 
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
    }
  };

  return (
    <>  
      {contactModalOpen && (
        <div 
          className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => {
            setContactModalOpen(false);
            setErrors({});
            setSubmissionSuccess(false);
          }}
        >
          <div 
            className="bg-white dark:bg-gray-900 rounded-xl max-w-6xl w-full p-8 shadow-2xl max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-2xl font-bold dark:text-white flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Contact Our Security Team
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  We typically respond within 1 business day
                </p>
              </div>
              <button
                onClick={() => {
                  setContactModalOpen(false);
                  setErrors({});
                  setSubmissionSuccess(false);
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close contact form"
              >
                <X size={24} />
              </button>
            </div>

            {/* Success Message */}
            {submissionSuccess && (
              <div className="mb-6 bg-green-50 dark:bg-green-900/10 border border-green-100 dark:border-green-900/20 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-green-800 dark:text-green-200">Message sent successfully!</h4>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Thank you for contacting us. We'll get back to you soon.
                  </p>
                </div>
              </div>
            )}

            {/* Submission Error Message */}
            {errors.submit && (
              <div className="mb-6 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-red-800 dark:text-red-200">Submission Error</h4>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                    {errors.submit}
                  </p>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <form onSubmit={handleContactSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label
                        htmlFor="fullName"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border ${errors.fullName ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all`}
                        placeholder="John Doe"
                      />
                      {errors.fullName && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.fullName}</p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all`}
                        placeholder="john@company.com"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="companyName"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all"
                      placeholder="Acme Inc."
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="serviceType"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Inquiry Type *
                    </label>                  
                   
                      <select
                          name="serviceType"
                          value={formData.serviceType}
                          onChange={handleChange}
                          required
                          className={`w-full px-4 py-3 bg-gray-800 border ${errors.serviceType ? 'border-red-500' : 'border-gray-700'} rounded-xl focus:ring-2 focus:ring-blue-500 text-white`}
                      >
                          <option value="">Select Service</option>
                          {serviceTypes.map(type => (
                              <option key={type} value={type}>{type}</option>
                          ))}
                      </select>
                      {errors.serviceType && <p className="text-red-500 text-sm mt-1 text-center">{errors.serviceType}</p>}
                  
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Message *
                    </label>
                    <textarea
                      name="message"
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border ${errors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white transition-all`}
                      placeholder="How can we help you?"
                    ></textarea>
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.message}</p>
                    )}
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                      {formData.message.length}/10,000 characters
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setContactModalOpen(false);
                        setErrors({});
                        setSubmissionSuccess(false);
                      }}
                      className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>

              <div className="border-l border-gray-200 dark:border-gray-700 pl-8">
                <div className="sticky top-0 space-y-8">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Contact Information
                    </h4>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full mt-0.5">
                          <Mail size={18} className="text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-white">General Inquiries</h5>
                          <a href="mailto:info@0x10layerff.com" className="text-blue-600 dark:text-blue-400 hover:underline text-sm">
                            info@layerff.tech
                          </a>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full mt-0.5">
                          <Shield size={18} className="text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-white">Security Team</h5>
                          <a href="mailto:security@0x10layerff.com" className="text-blue-600 dark:text-blue-400 hover:underline text-sm">
                          info@layerff.tech
                          </a>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            For vulnerability reports and security incidents
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full mt-0.5">
                          <Phone size={18} className="text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-white">Phone Support</h5>
                          <a href="tel:+15551234567" className="text-blue-600 dark:text-blue-400 hover:underline text-sm">
                            +256 774708048
                          </a>
                          <a href="tel:+15551234567" className="text-blue-600 dark:text-blue-400 hover:underline text-sm">
                            +256 772886750
                          </a>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            24/7
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Headquarters
                    </h4>
                    <div className="flex items-start gap-3">
                      <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full mt-0.5">
                        <MapPin size={18} className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-gray-700 dark:text-gray-300">
                          123 Cybersecurity Plaza<br />
                          Suite 1600<br />
                          San Francisco, CA 94107<br />
                          United States
                        </p>
                        <a 
                          href="#" 
                          className="inline-block mt-3 text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium"
                        >
                          View on map →
                        </a>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Emergency Contact
                    </h4>
                    <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-lg border border-red-100 dark:border-red-900/20">
                      <div className="flex items-start gap-3">
                        <div className="bg-red-100 dark:bg-red-900/20 p-2 rounded-full">
                          <AlertTriangle size={18} className="text-red-600 dark:text-red-400" />
                        </div>
                        <div>
                          <h5 className="font-medium text-gray-900 dark:text-white">Security Incident</h5>
                          <a href="mailto:abuse@0x10layerff.com" className="text-red-600 dark:text-red-400 hover:underline text-sm">
                            abuse@0x10layerff.com
                          </a>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            For immediate assistance with security breaches or abuse reports
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactUs;